import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ToastOptions } from '../../types';
import ToastContainer from './ToastContainer';

interface Toast extends ToastOptions {
  id: string;
  createdAt: number;
}

interface ToastContextType {
  addToast: (options: Omit<ToastOptions, 'id'>) => string;
  removeToast: (id: string) => void;
  removeAllToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  maxToasts = 5
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((options: Omit<ToastOptions, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const toast: Toast = {
      ...options,
      id,
      createdAt: Date.now(),
      duration: options.duration ?? 5000,
      position: options.position ?? 'top-right'
    };

    setToasts(prev => {
      const newToasts = [toast, ...prev].slice(0, maxToasts);
      return newToasts;
    });

    return id;
  }, [maxToasts]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const removeAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, removeAllToasts }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};